import { on } from "common/utils/ui/event";
import { observeScrollAnimation } from "common/utils/ui/visibility";
import { trackEvent } from "../made-category-print-analytics";
import "./delivery-and-quality.scss";

const BACKDROP_CLOSED_CLASSES = ["opacity-0", "invisible"];
const BACKDROP_OPEN_CLASSES = ["opacity-100", "visible"];
const DRAWER_CLOSED_CLASSES = ["translate-y-full", "md:translate-x-full"];
const DRAWER_OPEN_CLASSES = ["translate-y-0", "md:translate-x-0"];
const HERO_VIDEO_ENDED_CLASS = "delivery-quality-hero-video-ended";

function trackDrawerNavigation(direction) {
  trackEvent("clp_delivery_quality_drawer_navigated", {
    section_identifier: "delivery_and_quality",
    direction,
    eventLabel: direction
  });
}

class DeliveryAndQuality {
  #drawerOpen = false;

  #previousBodyOverflow = "";

  #bodyOverflowWhileDrawerOpen = "";

  constructor($root) {
    this.$root = $root;
    this.currentIndex = 0;
    this.contents = [];
    this.dots = [];

    this.handleDotClick = this.handleDotClick.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handlePrev = this.handlePrev.bind(this);
    this.handleNext = this.handleNext.bind(this);
  }

  get $backdrop() {
    return this.$root?.querySelector(".js-delivery-quality-drawer-backdrop");
  }

  get $drawer() {
    return this.$root?.querySelector(".js-delivery-quality-drawer");
  }

  get $drawerImage() {
    return this.$root?.querySelector(".js-delivery-quality-drawer-image");
  }

  get $drawerVideo() {
    return this.$root?.querySelector(".js-delivery-quality-drawer-video");
  }

  get $drawerTitle() {
    return this.$root?.querySelector(".js-delivery-quality-drawer-title");
  }

  get $drawerSubtitle() {
    return this.$root?.querySelector(".js-delivery-quality-drawer-subtitle");
  }

  get $scrollAnimateElements() {
    return this.$root?.querySelectorAll(".js-delivery-quality-header");
  }

  openDrawer() {
    if (this.#drawerOpen) return;
    this.#previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    this.#bodyOverflowWhileDrawerOpen = document.body.style.overflow;
    this.#drawerOpen = true;
    window.tidioChatApi?.hide?.();
    if (this.$backdrop) {
      this.$backdrop.classList.remove(...BACKDROP_CLOSED_CLASSES);
      this.$backdrop.classList.add(...BACKDROP_OPEN_CLASSES);
    }
    if (this.$drawer) {
      this.$drawer.classList.remove(...DRAWER_CLOSED_CLASSES);
      this.$drawer.classList.add(...DRAWER_OPEN_CLASSES);
    }
  }

  closeDrawer() {
    if (!this.#drawerOpen) return;
    if (document.body.style.overflow === this.#bodyOverflowWhileDrawerOpen) {
      document.body.style.overflow = this.#previousBodyOverflow ?? "";
    }
    this.#bodyOverflowWhileDrawerOpen = "";
    this.#drawerOpen = false;
    window.tidioChatApi?.show?.();
    if (this.$backdrop) {
      this.$backdrop.classList.remove(...BACKDROP_OPEN_CLASSES);
      this.$backdrop.classList.add(...BACKDROP_CLOSED_CLASSES);
    }
    if (this.$drawer) {
      this.$drawer.classList.remove(...DRAWER_OPEN_CLASSES);
      this.$drawer.classList.add(...DRAWER_CLOSED_CLASSES);
    }
  }

  updateDrawerContent(index) {
    const content = this.contents[index];
    if (!content) return;

    const mediaUrl = content.video || content.image;
    const isVideo = Boolean(content.video);

    if (this.$drawerImage) {
      this.$drawerImage.src = isVideo ? "" : mediaUrl;
      this.$drawerImage.alt = content.title;
      this.$drawerImage.classList.toggle("hidden", isVideo || !mediaUrl);
    }
    if (this.$drawerVideo) {
      this.$drawerVideo.src = isVideo ? mediaUrl : "";
      this.$drawerVideo.classList.toggle("hidden", !isVideo || !mediaUrl);
      if (isVideo && mediaUrl) {
        this.$drawerVideo.load();
        this.$drawerVideo.play().catch(() => {});
      } else {
        this.$drawerVideo.pause();
      }
    }
    if (this.$drawerTitle) this.$drawerTitle.textContent = content.title;
    if (this.$drawerSubtitle) this.$drawerSubtitle.textContent = content.subtitle;
  }

  initHeroVideo() {
    const $heroMedia = this.$root.querySelector(".js-delivery-quality-hero-media");
    const $heroVideo = this.$root.querySelector(".js-delivery-quality-main-video");
    if (!$heroMedia || !$heroVideo) return;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if ($heroMedia.classList.contains(HERO_VIDEO_ENDED_CLASS)) return;
        $heroVideo.play().catch(() => {});
      },
      { rootMargin: "0px", threshold: 0.25 }
    );
    observer.observe($heroMedia);
    $heroVideo.addEventListener("ended", () => {
      $heroMedia.classList.add(HERO_VIDEO_ENDED_CLASS);
    });
  }

  handleDotClick(event, $dot) {
    event.preventDefault();
    this.currentIndex = this.dots.indexOf($dot);
    this.updateDrawerContent(this.currentIndex);
    this.openDrawer();
  }

  handleClose() {
    this.closeDrawer();
  }

  handlePrev() {
    this.currentIndex = (this.currentIndex - 1 + this.contents.length) % this.contents.length;
    this.updateDrawerContent(this.currentIndex);
    trackDrawerNavigation("left");
  }

  handleNext() {
    this.currentIndex = (this.currentIndex + 1) % this.contents.length;
    this.updateDrawerContent(this.currentIndex);
    trackDrawerNavigation("right");
  }

  bindDrawerListeners() {
    on(
      "click",
      ".js-delivery-quality-dot",
      (e, $dot) => this.handleDotClick(e, $dot),
      false,
      this.$root
    );
    on("click", ".js-delivery-quality-drawer-backdrop", this.handleClose, false, this.$root);
    on("click", ".js-delivery-quality-drawer-close", this.handleClose, false, this.$root);
    on("click", ".js-delivery-quality-drawer-prev", this.handlePrev, false, this.$root);
    on("click", ".js-delivery-quality-drawer-next", this.handleNext, false, this.$root);
  }

  initScrollAnimation() {
    observeScrollAnimation(this.$scrollAnimateElements);
  }

  init() {
    this.initHeroVideo();
    this.initScrollAnimation();

    this.dots = Array.from(this.$root.querySelectorAll(".js-delivery-quality-dot"));
    this.contents = this.dots.map($dot => ({
      image: $dot.dataset.drawerImage ?? "",
      video: $dot.dataset.drawerVideo ?? "",
      title: $dot.dataset.drawerTitle ?? "",
      subtitle: $dot.dataset.drawerSubtitle ?? ""
    }));

    this.bindDrawerListeners();
  }
}

const initializedRoots = new WeakSet();

export default () => {
  const $root = document.querySelector(".made-category-print-delivery-and-quality");
  if (!$root || initializedRoots.has($root)) return;
  initializedRoots.add($root);
  new DeliveryAndQuality($root).init();
};
